/**
 * El perfil: quién eres y cómo vas.
 *
 * Existía un hueco raro: nombre y avatar se pedían en la pantalla 4 del
 * alta y **después ya no se podían cambiar nunca**. El endpoint estaba
 * (`POST /api/me`), pero solo el flujo de signup lo llamaba, así que
 * quien se equivocaba al escribir su nombre se quedaba con la errata para
 * siempre.
 *
 * Lo que se enseña aquí es lo que importa durante la Era Pionera:
 *
 * - **El badge Pionero**, si lo tiene. El nivel está congelado en
 *   Novato/Explorador (ADR-007) y el ordinal es el estatus real.
 * - **El progreso hacia el próximo pase.** ADR-013: lo que cuenta son
 *   recomendaciones con Visit Token, no pines.
 *
 * Lo que NO se enseña: el `reputationScore`. Existe en el modelo y
 * mostrarlo invitaría a optimizarlo, que es justo lo contrario de lo que
 * este producto quiere de sus primeros usuarios.
 */

import { useState } from 'react';
import { DISPLAY_NAME_MAX, DISPLAY_NAME_MIN } from '@gu/contracts';

import { ApiError, saveProfile, type Me } from '../../shared/lib/api.js';
import { Boton, Campo, Pantalla, Stack, Tarjeta, Texto, Titulo } from '../../shared/ui/primitives.js';
import { AVATAR_GALLERY } from './avatar.js';

export interface ProfileProps {
  perfil: Me;
  /** Refresca la sesión con el perfil ya guardado. */
  onGuardado: (perfil: Me) => void;
  onVolver: () => void;
}

export function Profile({ perfil, onGuardado, onVolver }: ProfileProps) {
  const [editando, setEditando] = useState(false);

  return (
    <Pantalla>
      <Titulo>Tu perfil</Titulo>

      {editando ? (
        <EditarPerfil
          perfil={perfil}
          onGuardado={(p) => {
            onGuardado(p);
            setEditando(false);
          }}
          onCancelar={() => setEditando(false)}
        />
      ) : (
        <VerPerfil perfil={perfil} onEditar={() => setEditando(true)} />
      )}

      <Boton variante="secundario" onClick={onVolver}>
        Volver al mapa
      </Boton>
    </Pantalla>
  );
}

/* ─────────────────────────────── Ver ─────────────────────────────── */

function VerPerfil({ perfil, onEditar }: { perfil: Me; onEditar: () => void }) {
  return (
    <Stack gap="var(--gu-space-5)">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--gu-space-4)',
        }}
      >
        {perfil.avatarUrl && (
          <img
            src={perfil.avatarUrl}
            // Vacío a propósito: el nombre está justo al lado y un lector
            // de pantalla que lea "avatar de Héctor. Héctor" es peor que
            // uno que salte la imagen decorativa.
            alt=""
            width={72}
            height={72}
            style={{ borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
          />
        )}
        <div>
          <Titulo variante="h2" nivel={2}>
            {perfil.displayName ?? 'Sin nombre'}
          </Titulo>
          {perfil.pioneroOrdinal !== null && (
            <p
              style={{
                margin: `var(--gu-space-1) 0 0`,
                color: 'var(--gu-text-primary)',
                fontWeight: 600,
              }}
            >
              {/*
               * El estatus de la Era Pionera. Es permanente y ordinal:
               * "#014" no se puede volver a ganar ni comprar, y por eso
               * va con el nombre y no escondido en una lista de logros.
               */}
              Pionero GU #{String(perfil.pioneroOrdinal).padStart(3, '0')}
            </p>
          )}
        </div>
      </div>

      <Boton onClick={onEditar}>Cambiar nombre o cara</Boton>

      <Tarjeta>
        <Stack gap="var(--gu-space-2)">
          <Texto variante="caption">Tus pases</Texto>
          <Titulo variante="h2" nivel={2}>
            {perfil.invitesLeft}
          </Titulo>
          <Texto variante="secundario">
            {perfil.invitesLeft > 0
              ? 'Cada pase trae a alguien. Se reponen al publicar.'
              : /*
                 * ADR-013: el gate son recomendaciones con Visit Token, no
                 * pines. Decir "lugares" aquí prometería un pase que el
                 * servidor no va a dar.
                 */
                'Publica tus opiniones para desbloquear pases.'}
          </Texto>
        </Stack>
      </Tarjeta>
    </Stack>
  );
}

/* ───────────────────────────── Editar ───────────────────────────── */

function EditarPerfil({
  perfil,
  onGuardado,
  onCancelar,
}: {
  perfil: Me;
  onGuardado: (p: Me) => void;
  onCancelar: () => void;
}) {
  const [nombre, setNombre] = useState(perfil.displayName ?? '');
  const [avatar, setAvatar] = useState(perfil.avatarUrl);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const valido =
    nombre.trim().length >= DISPLAY_NAME_MIN &&
    nombre.trim().length <= DISPLAY_NAME_MAX &&
    !!avatar;

  async function guardar() {
    if (!valido || !avatar) return;

    setOcupado(true);
    setError(null);
    try {
      onGuardado(await saveProfile({ displayName: nombre.trim(), avatarUrl: avatar }));
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : 'No pudimos guardarlo. Revisa tu señal e inténtalo otra vez.',
      );
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Stack gap="var(--gu-space-5)">
      <Campo
        id="nombre"
        etiqueta="¿Cómo te llamas?"
        autoComplete="nickname"
        valor={nombre}
        onChange={setNombre}
        error={error ?? undefined}
      />

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend
          style={{
            fontSize: 'var(--gu-text-body-sm)',
            color: 'var(--gu-text-secondary)',
            fontWeight: 550,
            padding: 0,
          }}
        >
          Tu cara
        </legend>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))',
            gap: 'var(--gu-space-3)',
            marginTop: 'var(--gu-space-3)',
          }}
        >
          {AVATAR_GALLERY.map((a) => (
            <label
              key={a.id}
              style={{
                display: 'block',
                cursor: 'pointer',
                borderRadius: '50%',
                aspectRatio: '1',
                outline:
                  avatar === a.src
                    ? '3px solid var(--gu-primary-strong)'
                    : '1px solid var(--gu-border-subtle)',
                outlineOffset: avatar === a.src ? '2px' : '0',
              }}
            >
              <input
                type="radio"
                name="avatar"
                value={a.src}
                checked={avatar === a.src}
                onChange={() => setAvatar(a.src)}
                // Fuera de pantalla, no `display:none`: así sigue siendo
                // enfocable con teclado y anunciable.
                style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
              />
              <img
                src={a.src}
                alt={a.alt}
                loading="lazy"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  borderRadius: '50%',
                }}
              />
            </label>
          ))}
        </div>
      </fieldset>

      <Boton onClick={() => void guardar()} disabled={ocupado || !valido}>
        {ocupado ? 'Guardando…' : 'Guardar'}
      </Boton>

      <Boton variante="secundario" onClick={onCancelar}>
        Cancelar
      </Boton>
    </Stack>
  );
}
